import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import { Download, ChevronDown, FileSpreadsheet, FileText, Music } from 'lucide-react';
import { GENRE_MAP } from '../data/genres';

function fmtPhone(c) {
  return [c?.phone1, c?.phone2, c?.phone3].filter(Boolean).join('-');
}
function fmtEmail(c) {
  if (!c?.emailId || !c?.emailDomain) return '';
  return `${c.emailId}@${c.emailDomain}`;
}
function fmtDateKo(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d).slice(0, 10);
  return `${dt.getFullYear()}년 ${dt.getMonth()+1}월 ${dt.getDate()}일`;
}
function fmtDateHeader(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d).slice(0, 10);
  const yy = String(dt.getFullYear()).slice(2);
  const mm = String(dt.getMonth()+1).padStart(2,'0');
  const dd = String(dt.getDate()).padStart(2,'0');
  return `${yy}. ${mm}. ${dd}.`;
}


export default function ReviewViewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { me } = useSelector(authSelector.selectAuthState);
  const params = new URLSearchParams(location.search);
  const currentPage = parseInt(params.get('page')) || 1;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [broadcasters, setBroadcasters] = useState([]);
  const [results, setResults] = useState({});
  const [currentDetailId, setCurrentDetailId] = useState(null);

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkResults, setBulkResults] = useState({});

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [dlOpen, setDlOpen] = useState(false);
  const [dlLoading, setDlLoading] = useState('');
  const dlRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/reviewApply/${id}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
        alert('데이터 불러오기 실패');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // 드롭다운 외부 클릭 닫기
  useEffect(() => {
    const handler = (e) => { if (dlRef.current && !dlRef.current.contains(e.target)) setDlOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-dvh">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
        <div className="text-sm text-slate-500">불러오는 중...</div>
      </div>
    );
  }

  if (!data) return null;

  const { reviewApply, album, client, songs, memberReviewAlbum, member } = data;

  // member 제출건은 memberReviewAlbum에 앨범/아티스트 정보가 있음
  const albumName = album?.nameKr || memberReviewAlbum?.albumName || '';
  const artistName = album?.artist?.nameKr || memberReviewAlbum?.artistName || '';
  const releaseDate = album?.releaseDateTime || memberReviewAlbum?.releaseDate || '';
  const typeId = album?.albumTypeId || memberReviewAlbum?.albumTypeId;
  const albumTypeLabel = typeId === 1 ? '싱글'
    : typeId === 2 ? 'EP'
    : typeId === 3 ? '정규'
    : typeId === 4 ? '컴필레이션'
    : '';
  const physicalYn = album ? (album.physicalAlbumYn === 'Y' ? 'Y' : 'N') : (memberReviewAlbum?.physicalAlbumYn === 'Y' ? 'Y' : 'N');
  const clientName = client?.name || memberReviewAlbum?.clientName || '';
  const memberName = member?.name || '';
  const riakId = client?.riakId || memberReviewAlbum?.riakId || '';
  const riakProducerName = client?.riakProducerName || memberReviewAlbum?.riakProducerName || '';

  // member 제출건의 곡 정보는 memberReviewAlbum.memberReviewSongs에 있음
  // songs 배열에 빈 객체만 있으면(song이 null) memberReviewSongs 사용
  const hasSongData = songs?.length > 0 && songs.some((s) => s.nameKr);
  const songList = hasSongData ? songs : (memberReviewAlbum?.memberReviewSongs || []);

  const hasWaiting = (songs || []).some((s) =>
    s.reviewApplyResults?.some((r) => r.status === 'WAITING')
  );
  const isAdmin = me?.adminYn === 'Y' || me?.superAdminYn === 'Y';

  // ──────────────────────────────────────────────────────────
  // 심의신청정보 Excel 다운로드
  // ──────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    setDlOpen(false); setDlLoading('excel');
    try {
      const ExcelJS = (await import('exceljs')).default || (await import('exceljs'));
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('심의신청정보');

      // 열 너비 (A~J)
      ws.columns = [
        { width: 16 }, { width: 18 }, { width: 16 }, { width: 28 },
        { width: 13 }, { width: 25 }, { width: 18 }, { width: 18 },
        { width: 18 }, { width: 28 },
      ];

      const THIN = { style: 'thin' };
      const border = { top: THIN, bottom: THIN, left: THIN, right: THIN };
      const labelFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      const center = { horizontal: 'center', vertical: 'middle', wrapText: true };
      const left   = { horizontal: 'left',   vertical: 'middle', wrapText: true };

      const stLabel = (cell, text) => {
        cell.value = text;
        cell.fill = labelFill; cell.border = border;
        cell.font = { bold: true, size: 10 };
        cell.alignment = center;
      };
      const stValue = (cell, text) => {
        cell.value = text ?? '';
        cell.border = border;
        cell.font = { size: 10 };
        cell.alignment = center;
      };

      // ── 행1: 신청자 + 연락처 + 이메일 (한 행)
      ws.getRow(1).height = 22;
      stLabel(ws.getCell('A1'), '신청자');
      stValue(ws.getCell('B1'), client?.name || clientName); ws.mergeCells('B1:C1');
      stLabel(ws.getCell('D1'), '연락처');
      stValue(ws.getCell('E1'), fmtPhone(client)); ws.mergeCells('E1:F1');
      stLabel(ws.getCell('G1'), '이메일');
      stValue(ws.getCell('H1'), fmtEmail(client)); ws.mergeCells('H1:J1');

      // ── 행2: 여백
      ws.getRow(2).height = 8;

      // ── 행3: 앨범 기본정보
      ws.getRow(3).height = 22;
      stLabel(ws.getCell('A3'), '아티스트명');
      stValue(ws.getCell('B3'), artistName); ws.mergeCells('B3:C3');
      stLabel(ws.getCell('D3'), '앨범명');
      stValue(ws.getCell('E3'), albumName); ws.mergeCells('E3:F3');
      stLabel(ws.getCell('G3'), '발매일');
      stValue(ws.getCell('H3'), fmtDateKo(releaseDate));
      stLabel(ws.getCell('I3'), '제작일');
      stValue(ws.getCell('J3'), fmtDateKo(reviewApply?.createdAt));

      // ── 행4: 음반형태/피지컬/장르/유통사
      ws.getRow(4).height = 22;
      stLabel(ws.getCell('A4'), '음반형태');
      stValue(ws.getCell('B4'), albumTypeLabel);
      stLabel(ws.getCell('C4'), '피지컬\n앨범 유/무');
      stValue(ws.getCell('D4'), physicalYn === 'Y' ? '유' : '무');
      stLabel(ws.getCell('E4'), '음반장르');
      stValue(ws.getCell('F4'), GENRE_MAP[album?.genreId] || ''); ws.mergeCells('F4:G4');
      stLabel(ws.getCell('H4'), '유통사');
      stValue(ws.getCell('I4'), '핑크페이퍼'); ws.mergeCells('I4:J4');

      // ── 행5: 제작사
      ws.getRow(5).height = 22;
      stLabel(ws.getCell('A5'), '제작사');
      stValue(ws.getCell('B5'), riakProducerName || clientName); ws.mergeCells('B5:J5');

      // ── 행6: 여백
      ws.getRow(6).height = 8;

      // ── 행7: 곡 테이블 헤더
      ws.getRow(7).height = 36;
      const songHeaders = [
        '타이틀곡', '우선순위', '트랙',
        '곡 제목',
        '피처링',
        '피처링 아티스트\n최근 발매 음원',
        '편곡', '작사', '작곡', '실연 (연주가/악기명)',
      ];
      const cols = ['A','B','C','D','E','F','G','H','I','J'];
      songHeaders.forEach((h, i) => {
        const cell = ws.getCell(`${cols[i]}7`);
        cell.value = h;
        cell.fill = headerFill; cell.border = border;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.alignment = center;
      });

      // ── 행8~: 곡 데이터 (곡 수만큼)
      const sortedSongs = [...songList].sort((a, b) => (a.track||0) - (b.track||0));
      for (let i = 0; i < sortedSongs.length; i++) {
        const s = sortedSongs[i];
        const rowIdx = 8 + i;
        ws.getRow(rowIdx).height = 20;
        const vals = s ? [
          s.titleYn === 'Y' ? 'O' : '',
          s.woneumYn === 'Y' ? 'O' : '',
          s.track || '',
          s.nameKr || '',
          s.featuring ? 'O' : '',
          s.featuringLatestRelease || '',
          s.arrangerKr || '',
          s.writerKr || '',
          s.composerKr || '',
          s.performance || '',
        ] : Array(10).fill('');
        vals.forEach((v, ci) => {
          const cell = ws.getCell(`${cols[ci]}${rowIdx}`);
          cell.value = v; cell.border = border;
          cell.alignment = center;
          cell.font = { size: 10 };
        });
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `심의신청정보_${artistName}_${albumName}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e); alert('엑셀 생성 실패');
    } finally { setDlLoading(''); }
  };

  // ──────────────────────────────────────────────────────────
  // 가사 Word 다운로드 (서버 사이드 생성)
  // ──────────────────────────────────────────────────────────
  const handleDownloadWord = async () => {
    setDlOpen(false); setDlLoading('word');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/reviewApply/${id}/download-word`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Word 생성 실패');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `가사_${artistName}_${albumName}.zip`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e); alert(e.message || 'Word 생성 실패');
    } finally { setDlLoading(''); }
  };

  // ──────────────────────────────────────────────────────────
  // 음원 zip 다운로드 (서버 → S3 → zip)
  // ──────────────────────────────────────────────────────────
  // ──────────────────────────────────────────────────────────
  // KBS 심의 파일 다운로드 (HWP + DOCX ZIP)
  // ──────────────────────────────────────────────────────────
  const handleDownloadKbs = async () => {
    setDlOpen(false); setDlLoading('kbs');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/reviewApply/${id}/download-kbs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'KBS 파일 생성 실패');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `KBS심의_${artistName}_${albumName}.zip`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e); alert(e.message || 'KBS 파일 생성 실패');
    } finally { setDlLoading(''); }
  };

  const handleDownloadAudio = async () => {
    setDlOpen(false); setDlLoading('audio');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/reviewApply/${id}/download-audio`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || '음원 다운로드 실패');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `음원_${artistName}_${albumName}.zip`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e); alert(e.message || '음원 다운로드 실패');
    } finally { setDlLoading(''); }
  };

  const DeleteReview = async () => {
    if (!reviewApply?.id) return;

    const ok = window.confirm('정말 삭제할까요? 삭제하면 복구할 수 없습니다.');
    if (!ok) return;

    try {
      setLoading(true);
      await axios.delete(`/reviewApply/${reviewApply.id}`);
      alert('삭제되었습니다.');

      // 목록으로 + 기존 페이지 유지
      navigate(`/ReviewPage?page=${currentPage}`);
    } catch (e) {
      console.error(e);
      alert('삭제 실패');
    } finally {
      setLoading(false);
    }
  };

  // 영수증 모달
  const openReceipt = async () => {
    if (!reviewApply?.id) return;

    try {
      setReceiptLoading(true);

      // 서버에서 영수증 URL 받아오기
      const res = await axios.get(`/reviewApply/${reviewApply.id}/receipt`);
      const url = res.data?.receiptUrl;

      if (!url) {
        alert('영수증 정보가 없습니다.');
        return;
      }

      setReceiptUrl(url);
      setReceiptOpen(true);
    } catch (e) {
      console.error(e);
      alert('영수증 불러오기 실패');
    } finally {
      setReceiptLoading(false);
    }
  };

  return (
    <>
      {/* 제목 */}
      <div className="mb-8 text-center">
          <div className="text-xl sm:text-2xl font-bold mb-3 text-white tracking-tight">신청서 조회</div>
        </div>

        <div className="space-y-6 max-w-3xl mx-auto">
          {/* 앨범 정보 */}
          <div className="grid grid-cols-1 gap-4">
            <ReadOnlyField
              label="신청일"
              value={reviewApply?.createdAt?.slice(0, 10)}
              extraClass="bg-amber-500/10 font-bold"
            />
            <ReadOnlyField
              label="진행상황"
              value={hasWaiting ? '심의중' : '심의완료'}
              extraClass={`bg-amber-500/10 font-bold ${
                hasWaiting
                  ? 'animate-pulse text-red-500'
                  : 'animate-pulse text-sky-500'
              }`}
            />
            {me?.adminYn === 'Y' && member && (
              <ReadOnlyField label="회원명" value={memberName} />
            )}
            {me?.adminYn === 'Y' && !member && (
              <ReadOnlyField label="권리사명" value={clientName} />
            )}
            {member && <ReadOnlyField label="권리사명" value={clientName} />}
            <ReadOnlyField label="앨범명" value={albumName} />
            <ReadOnlyField label="아티스트명" value={artistName} />
            <ReadOnlyField label="발매일" value={releaseDate} />
            <ReadOnlyField label="앨범 타입" value={albumTypeLabel} />
            <ReadOnlyField
              label="피지컬앨범 유무"
              value={physicalYn}
            />
            <ReadOnlyField label="RIAK 아이디" value={riakId} />
            <ReadOnlyField
              label="RIAK 제작사명"
              value={riakProducerName}
            />
          </div>

          {/* 구분선 */}
          <div className="flex items-center my-10">
            <div className="flex-grow border-t border-white/[0.08]"></div>
            <span className="px-3 text-slate-300 font-bold">곡 정보</span>
            <div className="flex-grow border-t border-white/[0.08]"></div>
          </div>

          {/* 곡 리스트 */}
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-6 bg-white/[0.03]">
            {!songList?.length && (
              <p className="text-sm text-slate-400">곡 정보가 없습니다.</p>
            )}

            {songList.map((s) => (
              <div
                key={s.id}
                className="rounded-xl border border-white/[0.08] p-4 space-y-3 bg-[#182334]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ReadOnlyField label="타이틀YN" value={s.titleYn} />
                  <ReadOnlyField label="트랙" value={s.track} />
                  <ReadOnlyField label="곡명" value={s.nameKr} />
                  <ReadOnlyField label="작사" value={s.writerKr} />
                  <ReadOnlyField label="작곡" value={s.composerKr} />
                  <ReadOnlyField label="편곡" value={s.arrangerKr} />
                </div>

                {s.fileUrl && (
                  <div>
                    <label className="block font-semibold mb-1">
                      음원 듣기
                    </label>
                    <audio controls src={s.fileUrl} style={{ width: '100%' }} />
                  </div>
                )}

                <ReadOnlyField label="피처링" value={s.featuring} />
                <ReadOnlyField
                  label="피처링 아티스트의 최근 음원"
                  value={s.featuringLatestRelease}
                />
                <ReadOnlyField
                  label="실연(연주자명/악기명)"
                  value={s.performance}
                />

                {!(s.nameKr || '').includes('Inst.') && (
                  <div>
                    <label className="block font-semibold mb-1">가사</label>
                    <textarea
                      value={s.lyrics || ''}
                      readOnly
                      className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300 focus:outline-none resize-none overflow-y-auto"
                      rows={5}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-semibold mb-1">
                      원음방송 선택
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold">
                      <input
                        type="checkbox"
                        checked={s.woneumYn === 'Y'}
                        disabled
                        className="peer hidden"
                      />
                      <span className="w-6 h-6 flex items-center justify-center border border-white/[0.08] rounded-full peer-checked:bg-sky-500 peer-checked:border-sky-500 text-white">
                        ✓
                      </span>
                      <span>원음방송</span>
                    </label>
                  </div>

                  <div className="flex items-center">
                    <button
                      onClick={async () => {
                        try {
                          const [bcRes, resultRes] = await Promise.all([
                            axios.get(`/reviewApply/broadcaster`),
                            axios.get(`/reviewApply/${s.reviewApplyDetailId}/result`),
                          ]);
                          const bcList = bcRes.data.map((b) => ({ id: b.id, name: b.name }));
                          setBroadcasters(bcList);
                          const init = {};
                          // 기존 결과 로드
                          resultRes.data.forEach((r) => {
                            init[r.broadcasterId] = { status: r.status, remark: r.remark };
                          });
                          // 결과 없는 방송사는 심의중(WAITING) 기본값
                          bcList.forEach((b) => {
                            if (!init[b.id]) init[b.id] = { status: 'WAITING', remark: '' };
                          });
                          setResults(init);
                          setCurrentDetailId(s.reviewApplyDetailId);
                          setResultModalOpen(true);
                        } catch (e) {
                          console.error(e);
                          alert('심의 결과 불러오기 실패');
                        }
                      }}
                      className={`w-full px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                        isAdmin
                          ? 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
                          : hasWaiting
                          ? 'bg-rose-500/20 text-rose-400'
                          : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
                      }`}>
                      {isAdmin ? '심의결과 입력' : hasWaiting ? '심의중' : '심의결과 확인'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 버튼 */}
          <div className="flex flex-wrap justify-between items-center gap-2 mt-6">
            {/* ── 다운로드 드롭다운 (관리자 전용) ── */}
            {(me?.adminYn === 'Y' || me?.superAdminYn === 'Y') && (
              <div className="relative" ref={dlRef}>
                <button
                  onClick={() => setDlOpen((p) => !p)}
                  disabled={!!dlLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 transition-colors">
                  <Download size={15} />
                  {dlLoading === 'excel' ? '엑셀 생성 중…'
                    : dlLoading === 'word' ? 'Word 생성 중…'
                    : dlLoading === 'audio' ? '음원 다운로드 중…'
                    : dlLoading === 'kbs' ? 'KBS 생성 중…'
                    : '다운로드'}
                  <ChevronDown size={13} className={`transition-transform ${dlOpen ? 'rotate-180' : ''}`} />
                </button>
                {dlOpen && (
                  <div className="absolute left-0 top-full mt-1 w-52 bg-[#1e2d42] border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                    <button
                      onClick={handleDownloadExcel}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition-colors">
                      <FileSpreadsheet size={14} className="text-emerald-400" />
                      심의신청정보 (Excel)
                    </button>
                    <button
                      onClick={handleDownloadWord}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition-colors">
                      <FileText size={14} className="text-sky-400" />
                      가사 (Word)
                    </button>
                    <button
                      onClick={handleDownloadAudio}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition-colors">
                      <Music size={14} className="text-amber-400" />
                      음원 (Zip)
                    </button>
                    <button
                      onClick={handleDownloadKbs}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition-colors">
                      <FileText size={14} className="text-rose-400" />
                      KBS 심의 (HWP+DOCX)
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* 일괄입력 버튼 (관리자 전용) */}
            {isAdmin && (
              <button
                onClick={async () => {
                  try {
                    const bcRes = await axios.get(`/reviewApply/broadcaster`);
                    const bcList = bcRes.data.map((b) => ({ id: b.id, name: b.name }));
                    setBroadcasters(bcList);
                    // 기존 저장된 결과 반영 (첫 번째 곡의 결과를 기준으로 초기화)
                    const existingResults = data?.songs?.[0]?.reviewApplyResults || [];
                    const initBulk = {};
                    bcList.forEach((b) => {
                      const saved = existingResults.find(r => r.broadcasterId === b.id);
                      initBulk[b.id] = { status: saved?.status || 'WAITING', remark: saved?.remark || '' };
                    });
                    setBulkResults(initBulk);
                    setBulkModalOpen(true);
                  } catch (e) {
                    alert('방송사 목록 불러오기 실패');
                  }
                }}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors">
                심의결과 일괄입력
              </button>
            )}

            {/* 오른쪽 버튼 그룹 */}
            <div className="flex flex-wrap items-center gap-2 ml-auto">
            {me?.adminYn === 'Y' && (
              <>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={reviewApply.showEditYn === 'Y'}
                    onChange={async (e) => {
                      try {
                        const newVal = e.target.checked ? 'Y' : 'N';
                        await axios.put(
                          `/reviewApply/${reviewApply.id}/showEdit`,
                          { showEditYn: newVal }
                        );
                        setData((prev) => ({
                          ...prev,
                          reviewApply: {
                            ...prev.reviewApply,
                            showEditYn: newVal,
                          },
                        }));
                      } catch (err) {
                        console.error(err);
                        alert('토글 저장 실패');
                      }
                    }}
                  />
                  <div className="w-12 h-6 bg-slate-500 rounded-full peer-checked:bg-sky-500 transition-colors"></div>
                  <span className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-6"></span>
                  <span className="ml-2 text-sm text-slate-200 whitespace-nowrap">
                    {reviewApply.showEditYn === 'Y'
                      ? '수정 ON'
                      : '수정 OFF'}
                  </span>
                </label>

                <button
                  onClick={() =>
                    navigate(
                      `/ReviewModifyPage/${reviewApply.id}?page=${currentPage}`
                    )
                  }
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 transition-colors">
                  수정
                </button>
              </>
            )}

            {me?.adminYn !== 'Y' && reviewApply.showEditYn === 'Y' && (
              <button
                onClick={() =>
                  navigate(
                    `/ReviewModifyPage/${reviewApply.id}?page=${currentPage}`
                  )
                }
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 transition-colors">
                수정
              </button>
            )}
            <button
              onClick={openReceipt}
              disabled={receiptLoading}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors">
              {receiptLoading ? '불러오는 중...' : '영수증'}
            </button>
            {me?.adminYn === 'Y' && (
              <button
                onClick={DeleteReview}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 transition-colors">
                삭제
              </button>
            )}
            <button
              onClick={() => navigate('/ReviewPage')}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
              목록
            </button>
            </div>{/* 오른쪽 버튼 그룹 끝 */}
        </div>
      </div>

      {/* ✅ 모달은 flex-1 div 밖으로 뺌 */}
      {resultModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]"
          onClick={() => setResultModalOpen(false)}>
          <div
            className="bg-[#182334] rounded-lg p-6 w-full max-w-2xl mx-4 flex flex-col space-y-4 border border-white/[0.08]"
            onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-center mb-4 text-white">
              {isAdmin ? '심의결과 입력' : '방송사별 심의 결과'}
            </h2>

            {isAdmin && (
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => {
                    const updated = {};
                    broadcasters.forEach((b) => { updated[b.id] = { ...results[b.id], status: 'Y' }; });
                    setResults(updated);
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors">
                  전체 통과
                </button>
                <button
                  onClick={() => {
                    const updated = {};
                    broadcasters.forEach((b) => { updated[b.id] = { ...results[b.id], status: 'N' }; });
                    setResults(updated);
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 transition-colors">
                  전체 거절
                </button>
              </div>
            )}

            <div className="space-y-3 max-h-[520px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:transparent [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full">
              {broadcasters.map((b) => (
                <div
                  key={b.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border border-white/[0.08] p-3 rounded-xl">
                  <span className="font-bold text-white text-sm">
                    📻 {b.name}
                  </span>
                  <div className="flex flex-col sm:flex-row gap-2 flex-1 justify-end items-center">
                    {isAdmin ? (
                      <>
                        {['WAITING', 'Y', 'N'].map((st) => (
                          <button
                            key={st}
                            onClick={() => setResults((prev) => ({ ...prev, [b.id]: { ...prev[b.id], status: st } }))}
                            className={`px-3 py-1 rounded-xl text-xs font-bold transition-colors ${
                              results[b.id]?.status === st
                                ? st === 'Y' ? 'bg-sky-500 text-white' : st === 'N' ? 'bg-red-500 text-white' : 'bg-slate-500 text-white'
                                : 'bg-white/5 text-slate-400 hover:bg-white/10'
                            }`}>
                            {st === 'Y' ? '통과' : st === 'N' ? '거절' : '심의중'}
                          </button>
                        ))}
                        <input
                          type="text"
                          placeholder="사유"
                          value={results[b.id]?.remark || ''}
                          onChange={(e) => setResults((prev) => ({ ...prev, [b.id]: { ...prev[b.id], remark: e.target.value } }))}
                          className="w-32 sm:w-40 px-2 py-1 text-sm rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 focus:outline-none"
                        />
                      </>
                    ) : (
                      <>
                        <span className={`px-3 py-1 rounded-xl text-white text-xs font-bold ${
                          results[b.id]?.status === 'Y' ? 'bg-sky-500' : results[b.id]?.status === 'N' ? 'bg-red-500' : 'bg-slate-500'
                        }`}>
                          {results[b.id]?.status === 'Y' ? '통과' : results[b.id]?.status === 'N' ? '거절' : '심의중'}
                        </span>
                        <input
                          type="text"
                          readOnly
                          value={results[b.id]?.remark || ''}
                          placeholder="사유"
                          className="w-40 sm:w-56 px-2 py-1 text-sm rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none"
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              {isAdmin && (
                <button
                  onClick={async () => {
                    try {
                      await axios.put(`/reviewApply/${currentDetailId}/result`, {
                        results: Object.entries(results).map(([broadcasterId, r]) => ({
                          broadcasterId: Number(broadcasterId),
                          status: r.status || 'WAITING',
                          remark: r.remark,
                        })),
                      });
                      alert('심의 결과가 저장되었습니다.');
                      setResultModalOpen(false);
                      const res = await axios.get(`/reviewApply/${id}`);
                      setData(res.data);
                    } catch (e) {
                      alert('저장 실패');
                    }
                  }}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 transition-colors">
                  저장
                </button>
              )}
              <button
                onClick={() => setResultModalOpen(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 심의결과 일괄입력 모달 */}
      {bulkModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]"
          onClick={() => setBulkModalOpen(false)}>
          <div
            className="bg-[#182334] rounded-lg p-6 w-full max-w-2xl mx-4 flex flex-col space-y-4 border border-white/[0.08]"
            onClick={(e) => e.stopPropagation()}>
            <div>
              <h2 className="text-lg font-bold text-center text-white">심의결과 일괄입력</h2>
              <p className="text-center text-xs text-slate-400 mt-1">전체 {songList.length}곡에 동일한 결과가 적용됩니다</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  const updated = {};
                  broadcasters.forEach((b) => { updated[b.id] = { ...bulkResults[b.id], status: 'Y' }; });
                  setBulkResults(updated);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors">
                전체 통과
              </button>
              <button
                onClick={() => {
                  const updated = {};
                  broadcasters.forEach((b) => { updated[b.id] = { ...bulkResults[b.id], status: 'N' }; });
                  setBulkResults(updated);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 transition-colors">
                전체 거절
              </button>
            </div>

            <div className="space-y-3 max-h-[520px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:transparent [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full">
              {broadcasters.map((b) => (
                <div
                  key={b.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border border-white/[0.08] p-3 rounded-xl">
                  <span className="font-bold text-white text-sm">📻 {b.name}</span>
                  <div className="flex flex-col sm:flex-row gap-2 flex-1 justify-end items-center">
                    {['WAITING', 'Y', 'N'].map((st) => (
                      <button
                        key={st}
                        onClick={() => setBulkResults((prev) => ({ ...prev, [b.id]: { ...prev[b.id], status: st } }))}
                        className={`px-3 py-1 rounded-xl text-xs font-bold transition-colors ${
                          bulkResults[b.id]?.status === st
                            ? st === 'Y' ? 'bg-sky-500 text-white' : st === 'N' ? 'bg-red-500 text-white' : 'bg-slate-500 text-white'
                            : 'bg-white/5 text-slate-400 hover:bg-white/10'
                        }`}>
                        {st === 'Y' ? '통과' : st === 'N' ? '거절' : '심의중'}
                      </button>
                    ))}
                    <input
                      type="text"
                      placeholder="사유"
                      value={bulkResults[b.id]?.remark || ''}
                      onChange={(e) => setBulkResults((prev) => ({ ...prev, [b.id]: { ...prev[b.id], remark: e.target.value } }))}
                      className="w-32 sm:w-40 px-2 py-1 text-sm rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 focus:outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={async () => {
                  try {
                    await axios.put(`/reviewApply/${reviewApply.id}/result-bulk`, {
                      results: Object.entries(bulkResults).map(([broadcasterId, r]) => ({
                        broadcasterId: Number(broadcasterId),
                        status: r.status || 'WAITING',
                        remark: r.remark,
                      })),
                    });
                    alert('전체 심의 결과가 저장되었습니다.');
                    setBulkModalOpen(false);
                    const res = await axios.get(`/reviewApply/${id}`);
                    setData(res.data);
                  } catch (e) {
                    alert('저장 실패');
                  }
                }}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition-colors">
                전체 저장
              </button>
              <button
                onClick={() => setBulkModalOpen(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 영수증 모달 */}
      {receiptOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]"
          onClick={() => setReceiptOpen(false)}>
          <div
            className="bg-[#182334] rounded-lg w-full max-w-3xl mx-4 overflow-hidden border border-white/[0.08]"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
              <div className="font-bold text-white">결제 영수증</div>
              <button
                className="text-slate-400 hover:text-white"
                onClick={() => setReceiptOpen(false)}>
                ✕
              </button>
            </div>

            <div className="w-full h-[75vh] bg-white/[0.03]">
              {!receiptUrl ? (
                <div className="h-full flex items-center justify-center text-slate-400">
                  영수증 URL이 없습니다.
                </div>
              ) : (
                <iframe
                  title="toss-receipt"
                  src={receiptUrl}
                  className="w-full h-full"
                />
              )}
            </div>

            <div className="px-4 py-3 border-t border-white/[0.08] flex justify-end">
              <button
                onClick={() => setReceiptOpen(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// 읽기 전용 input
function ReadOnlyField({ label, value, extraClass }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <input
        type="text"
        value={
          typeof value === 'string' || typeof value === 'number' ? value : ''
        }
        className={`w-full px-3 py-2 rounded-xl border bg-white/5 border-white/[0.08] text-slate-400 focus:outline-none ${
          extraClass || ''
        }`}
        readOnly
      />
      {typeof value !== 'string' && typeof value !== 'number' ? value : null}
    </div>
  );
}
